import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import Enable2FA from "../components/Enable2FA";

export default function DashboardPage() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const logout = useCallback(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("authToken");
        localStorage.removeItem("role");
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("authToken");
        sessionStorage.removeItem("role");
        navigate("/login");
    }, [navigate]);

    useEffect(() => {
        const controller = new AbortController();

        (async () => {
            try {
                const token =
                    localStorage.getItem("authToken") ||
                    sessionStorage.getItem("authToken") ||
                    localStorage.getItem("token") ||
                    sessionStorage.getItem("token");

                if (!token) return logout();

                const res = await api.get("/login/me", {
                    signal: controller.signal,
                });

                setUser(res.data);
            } catch (error) {
                if (error?.response?.status === 401 || error?.response?.status === 403) {
                    logout();
                    return;
                }

                console.error("Error loading user:", error);
                setError("Failed to load user data.");
            } finally {
                setLoading(false);
            }
        })();

        return () => controller.abort();
    }, [logout]);

    if (loading) {
        return <div className="p-6 text-slate-600">Loading dashboard...</div>;
    }

    if (error) {
        return (
            <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                {error}
            </div>
        );
    }

    const isBusiness =
        user?.userType === 1 || user?.userType === "LegalEntity";

    const fullName =
        [user?.name, user?.lastName].filter(Boolean).join(" ") ||
        user?.fullName ||
        user?.userName ||
        "Unknown";

    const twoFactorEnabled =
        user?.isTwoFactorEnabled ?? user?.IsTwoFactorEnabled ?? false;

    return (
        <div className="w-full">
            <div className="mb-8">
                <h1 className="text-3xl font-black text-slate-900">
                    Personal Account
                </h1>
                <p className="text-slate-600 mt-2">
                    Manage your profile, security settings and logistics activity.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
                <StatCard title="Account Type" value={isBusiness ? "Legal Entity" : "Individual"} />
                <StatCard title="2FA Status" value={twoFactorEnabled ? "Enabled" : "Disabled"} />
                <StatCard title="Platform" value="IDLogicChain" />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <div className="xl:col-span-2 bg-white rounded-2xl shadow border border-slate-200 p-6">
                    <h2 className="text-2xl font-bold mb-5">Profile Information</h2>

                    <div className="grid md:grid-cols-2 gap-4">
                        <InfoRow label="Name" value={fullName} />
                        <InfoRow label="Email" value={user?.email || "—"} />
                        <InfoRow label="Username" value={user?.userName || user?.username || "—"} />
                        <InfoRow label="User Type" value={isBusiness ? "Legal Entity" : "Individual"} />

                        {isBusiness && (
                            <>
                                <InfoRow label="Company" value={user?.companyName || "—"} />
                                <InfoRow label="Tax ID" value={user?.taxId || "—"} />
                                <InfoRow label="Registration Number" value={user?.registrationNumber || "—"} />
                            </>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow border border-slate-200 p-6">
                    <h2 className="text-2xl font-bold mb-5">Quick Actions</h2>

                    <div className="space-y-3">
                        <button
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-xl transition"
                            onClick={() => navigate("/client/orders")}
                        >
                            My Orders
                        </button>

                        <button
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-xl transition"
                            onClick={() => navigate("/client/orders/create")}
                        >
                            Create Order
                        </button>

                        <button
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition"
                            onClick={() => navigate("/client/shipments")}
                        >
                            My Shipments
                        </button>

                        <button
                            type="button"
                            className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-xl transition"
                            onClick={logout}
                        >
                            Log out
                        </button>
                    </div>
                </div>
            </div>

            <div className="mt-6 bg-white rounded-2xl shadow border border-slate-200 p-6">
                <h2 className="text-2xl font-bold mb-5">Security</h2>

                <Enable2FA
                    email={user?.email}
                    initialEnabled={twoFactorEnabled}
                    onStatusChange={(isEnabled) => {
                        setUser((u) =>
                            u ? { ...u, isTwoFactorEnabled: isEnabled } : u
                        );
                    }}
                />
            </div>
        </div>
    );
}

const StatCard = ({ title, value }) => (
    <div className="bg-white rounded-2xl shadow border border-slate-200 p-6">
        <p className="text-slate-500">{title}</p>
        <div className="inline-flex mt-3 px-3 py-1 rounded-xl font-black text-xl bg-blue-50 text-blue-700">
            {value}
        </div>
    </div>
);

const InfoRow = ({ label, value }) => (
    <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
        <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
            {label}
        </p>
        <p className="font-bold text-slate-900 mt-1 break-words">
            {value}
        </p>
    </div>
);